<script lang="ts">
  import { subscriptionStore, subscriptionType, trialInfo, openPricingModal, usageLimits } from '$lib/stores/subscription';
  import { SubscriptionDisplayService } from '$lib/services/subscription/SubscriptionDisplayService';
  import { SUBSCRIPTION_LIMITS } from '$lib/constants/subscription';
  import { demoTracker } from '$lib/stores/demoStore';

  function handleUpgradeClick() {
    if (demoTracker.isDemoMode()) {
      // In demo mode, trigger demo expired modal
      window.dispatchEvent(new CustomEvent('demo-expired', {
        detail: { feature: 'settings' }
      }));
    } else {
      openPricingModal();
    }
  }

  $: isDemo = demoTracker.isDemoMode();
  $: statusLabel = isDemo ? 'DEMO' : SubscriptionDisplayService.getStatusLabel($subscriptionType);
  $: expiryDate = SubscriptionDisplayService.formatDate($subscriptionStore?.validUntil);
  $: demoRemaining = isDemo ? demoTracker.getRemaining() : null;
</script>

<div class="status-card {isDemo ? 'demo' : $subscriptionType}">
  <div class="status-header">
    <div class="header-left">
      <span class="status-badge {isDemo ? 'demo' : $subscriptionType}">
        {statusLabel}
      </span>
      {#if isDemo && demoRemaining}
        <span class="demo-remaining">맞춤법 {demoRemaining.spellCheck}회 · AI {demoRemaining.ai}회</span>
      {:else if $subscriptionType === 'premium'}
        <span class="status-active">✓ 활성화됨</span>
      {:else if $subscriptionType === 'trial'}
        <span class="trial-days">{Math.ceil($trialInfo?.daysRemaining || 0)}일 남음</span>
      {:else if $subscriptionType === 'beta'}
        <span class="beta-tester">🧪 베타 테스터</span>
        {#if expiryDate}
          <span class="expiry-badge">{expiryDate}까지</span>
        {/if}
      {/if}
    </div>
    {#if isDemo || $subscriptionType !== 'premium'}
      <button class="upgrade-button-compact" on:click={handleUpgradeClick}>
        {isDemo ? '전체 버전 사용하기 →' : '프리미엄 업그레이드 →'}
      </button>
    {/if}
  </div>
  
  <div class="status-body">
    <div class="status-main-info">
      {#if isDemo}
        <h4>데모 체험 · 제한된 사용</h4>
        <p>맞춤법 검사 {demoRemaining?.spellCheck || 0}회, AI 기능 {demoRemaining?.ai || 0}회 남았습니다</p>
      {:else if $subscriptionType === 'premium'}
        <h4>무제한 사용 · 모든 기능 이용 가능</h4>
        <p>맞춤법 검사, AI 분석 모두 무제한으로 사용할 수 있습니다</p>
      {:else if $subscriptionType === 'trial'}
        <h4>프리미엄 체험 중 · 모든 기능 이용 가능</h4>
        <p>체험 기간 동안 모든 프리미엄 기능을 무료로 사용해보세요</p>
      {:else if $subscriptionType === 'beta'}
        <h4>베타 프로그램 · 확장된 기능 이용 가능</h4>
        <p>베타 테스터로서 맞춤법 검사 무제한, AI 분석 {SUBSCRIPTION_LIMITS.BETA.dailyAIAnalysisLimit}회/일 제한으로 사용하실 수 있습니다</p>
      {:else}
        <h4>무료 플랜 · 일일 사용 제한</h4>
        <p>기본 기능을 무료로 사용 중입니다</p>
      {/if}
    </div>

    <div class="status-description">
      {#if isDemo}
        <p>데모 모드에서 주요 기능을 체험 중입니다. 전체 기능을 사용하려면 앱을 다운로드하거나 웹 버전을 구독하세요.</p>
      {:else if $subscriptionType === 'premium'}
        <p>프리미엄 구독이 활성화되어 모든 기능을 제한 없이 사용할 수 있습니다.</p>
      {:else if $subscriptionType === 'trial'}
        <p>무료 체험 기간 동안 프리미엄 기능을 사용해보세요.</p>
      {:else if $subscriptionType === 'beta'}
        <p>베타 프로그램에 참여해주셔서 감사합니다. 정식 출시 전까지 프리미엄 기능을 테스트해보세요.</p>
      {:else}
        <p>무료 플랜으로 기본 기능을 사용 중입니다. 더 많은 기능을 원하시면 업그레이드하세요.</p>
      {/if}
    </div>
    
    {#if $subscriptionType === 'premium' && expiryDate}
      <div class="expiry-info">
        <span class="expiry-label">만료일:</span>
        <span class="expiry-date">{expiryDate}</span>
      </div>
    {/if}
  </div>
</div>

<style>
  .status-card {
    background: var(--bg-secondary);
    border: 1px solid var(--border-primary);
    border-radius: 12px;
    padding: 24px;
    margin-top: 16px;
  }
  
  .status-card.premium {
    background: linear-gradient(135deg, rgba(139, 92, 246, 0.02), rgba(99, 102, 241, 0.02));
    border-color: rgba(139, 92, 246, 0.2);
  }
  
  .status-card.trial {
    background: linear-gradient(135deg, rgba(245, 158, 11, 0.02), rgba(249, 115, 22, 0.02));
    border-color: rgba(245, 158, 11, 0.2);
  }

  .status-card.beta {
    background: linear-gradient(135deg, rgba(16, 185, 129, 0.02), rgba(5, 150, 105, 0.02));
    border-color: rgba(16, 185, 129, 0.2);
  }

  .status-card.demo {
    background: linear-gradient(135deg, rgba(147, 51, 234, 0.02), rgba(126, 34, 206, 0.02));
    border-color: rgba(147, 51, 234, 0.2);
  }

  .status-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 16px;
    border-bottom: 1px solid var(--border-secondary);
    margin-bottom: 20px;
  }
  
  .header-left {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  
  .status-badge {
    padding: 6px 14px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    display: inline-flex;
    align-items: center;
  }
  
  .status-badge.premium {
    background: linear-gradient(135deg, #8b5cf6, #6366f1);
    color: white;
  }
  
  .status-badge.trial {
    background: linear-gradient(135deg, #f59e0b, #f97316);
    color: white;
  }
  
  .status-badge.beta {
    background: linear-gradient(135deg, #10b981, #059669);
    color: white;
  }

  .status-badge.free {
    background: var(--bg-tertiary);
    border: 1px solid var(--border-secondary);
    color: var(--text-secondary);
  }

  .status-active {
    font-size: 12px;
    font-weight: 600;
    color: var(--success);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  .trial-days {
    font-size: 13px;
    font-weight: 600;
    color: var(--warning);
    padding: 4px 10px;
    background: rgba(245, 158, 11, 0.1);
    border-radius: 12px;
  }

  .beta-tester {
    font-size: 13px;
    font-weight: 600;
    color: #10b981;
    padding: 4px 10px;
    background: rgba(16, 185, 129, 0.1);
    border-radius: 12px;
  }

  .expiry-badge {
    font-size: 12px;
    font-weight: 600;
    color: #6b7280;
    padding: 4px 10px;
    background: rgba(107, 114, 128, 0.1);
    border-radius: 12px;
  }

  .upgrade-button-compact {
    padding: 8px 16px;
    background: linear-gradient(135deg, var(--accent-primary), var(--accent-secondary));
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s;
  }
  
  .upgrade-button-compact:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(124, 58, 237, 0.3);
  }
  
  .status-body {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
  
  .status-main-info h4 {
    font-size: 16px;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0 0 6px 0;
  }
  
  .status-main-info p {
    font-size: 14px;
    color: var(--text-secondary);
    margin: 0;
  }
  
  .status-description {
    padding: 12px 16px;
    background: var(--bg-tertiary);
    border-radius: 8px;
  }
  
  .status-description p {
    font-size: 13px;
    color: var(--text-secondary);
    margin: 0;
    line-height: 1.5;
  }

  .expiry-info {
    padding-top: 16px;
    border-top: 1px solid var(--border-secondary);
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
  }
  
  .expiry-label {
    color: var(--text-tertiary);
  }
  
  .expiry-date {
    color: var(--text-primary);
    font-weight: 500;
  }
</style>